import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { authService } from '../../auth/authService';
import { ROLES, hasAnyRole } from '../../utils/roleUtils';
import {
  buildTatNotificationHref,
  formatTatNotificationContent,
  getTatUnreadCount,
  listTatNotifications,
  markAllTatNotificationsRead,
  markTatNotificationRead,
  TatNotification,
} from '../../services/tatNotificationService';
import {
  buildPeerReviewNotificationHref,
  getPeerReviewEventLabel,
  getPeerReviewUnreadCount,
  listPeerReviewNotifications,
  markAllPeerReviewNotificationsRead,
  markPeerReviewNotificationRead,
  PeerReviewNotification,
} from '../../services/peerReviewNotificationService';
import './TatNotificationBell.css';

const POLL_INTERVAL_MS = 60000;

type UnifiedNotification =
  | { kind: 'tat'; createdAt: string; data: TatNotification }
  | { kind: 'peer'; createdAt: string; data: PeerReviewNotification };

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) {
    return 'Just now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return date.toLocaleDateString();
}

function canShowNotificationBell(): boolean {
  const user = authService.getUser();
  return hasAnyRole(user, [
    ROLES.RIS_ADMIN,
    ROLES.RIS_RADIOLOGIST,
    ROLES.SUPER_ADMIN,
    ROLES.RIS_HUB_ADMIN,
    ROLES.RIS_DISTRICT_ADMIN,
    ROLES.RIS_RADIOLOGY_MANAGER,
  ]);
}

function sortByCreatedAtDesc(a: UnifiedNotification, b: UnifiedNotification): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function TatNotificationListItem({
  onActivate,
  onDismiss,
  children,
}: Readonly<{
  onActivate: () => void;
  onDismiss: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}>) {
  return (
    <div className="tat-notification-item">
      <button
        type="button"
        className="tat-notification-item-main"
        onClick={onActivate}
      >
        {children}
      </button>
      <button
        type="button"
        className="tat-notification-dismiss"
        aria-label="Dismiss notification"
        onClick={onDismiss}
      >
        <X
          size={14}
          strokeWidth={2}
        />
      </button>
    </div>
  );
}

const TatNotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<UnifiedNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDialogElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const loadUnreadCount = useCallback(async () => {
    if (!canShowNotificationBell()) {
      return;
    }
    try {
      const [tatCount, peerCount] = await Promise.all([
        getTatUnreadCount().catch(() => 0),
        getPeerReviewUnreadCount().catch(() => 0),
      ]);
      setUnreadCount(tatCount + peerCount);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!canShowNotificationBell()) {
      return;
    }
    setLoading(true);
    try {
      const [tatRows, peerRows] = await Promise.all([
        listTatNotifications(50, { unreadOnly: true }).catch(() => [] as TatNotification[]),
        listPeerReviewNotifications(50, { unreadOnly: true }).catch(
          () => [] as PeerReviewNotification[]
        ),
      ]);

      const unified: UnifiedNotification[] = [
        ...tatRows.map(row => ({
          kind: 'tat' as const,
          createdAt: row.breach_detected_at || row.created_at,
          data: row,
        })),
        ...peerRows.map(row => ({
          kind: 'peer' as const,
          createdAt: row.created_at,
          data: row,
        })),
      ].sort(sortByCreatedAtDesc);

      setNotifications(unified);
      await loadUnreadCount();
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [loadUnreadCount]);

  useEffect(() => {
    if (!canShowNotificationBell()) {
      return undefined;
    }
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    loadNotifications();

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, loadNotifications]);

  const removeNotification = (kind: 'tat' | 'peer', id: number) => {
    setNotifications(prev => prev.filter(row => !(row.kind === kind && row.data.id === id)));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markNotificationRead = async (item: UnifiedNotification) => {
    if (item.data.is_read) {
      return;
    }
    if (item.kind === 'tat') {
      await markTatNotificationRead(item.data.id);
    } else {
      await markPeerReviewNotificationRead(item.data.id);
    }
    removeNotification(item.kind, item.data.id);
  };

  const handleBellClick = () => {
    setOpen(prev => !prev);
  };

  const handleNotificationClick = (item: UnifiedNotification) => {
    setOpen(false);
    const href =
      item.kind === 'tat'
        ? buildTatNotificationHref(item.data)
        : buildPeerReviewNotificationHref(item.data);
    navigate(href);
    void markNotificationRead(item).catch(() => {
      // navigation already happened; ignore mark-read failures
    });
  };

  const handleDismiss = async (
    event: React.MouseEvent<HTMLButtonElement>,
    item: UnifiedNotification
  ) => {
    event.stopPropagation();
    try {
      await markNotificationRead(item);
    } catch {
      removeNotification(item.kind, item.data.id);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await Promise.all([
        markAllTatNotificationsRead().catch(() => undefined),
        markAllPeerReviewNotificationsRead().catch(() => undefined),
      ]);
      setNotifications([]);
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  if (!canShowNotificationBell()) {
    return null;
  }

  return (
    <div className="tat-notification-bell-wrap">
      <button
        ref={buttonRef}
        type="button"
        aria-label="Notifications"
        className="page-header-notification-btn"
        onClick={handleBellClick}
      >
        <Bell
          size={22}
          strokeWidth={2}
        />
        {unreadCount > 0 && (
          <span className="page-header-notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open ? (
        <dialog
          ref={panelRef}
          open
          className="tat-notification-panel"
          aria-label="Notifications"
        >
          <div className="tat-notification-panel-header">
            <span className="tat-notification-panel-title">Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="tat-notification-mark-all"
                onClick={handleMarkAllRead}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="tat-notification-panel-body">
            {loading ? (
              <div className="tat-notification-empty">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="tat-notification-empty">No unread notifications</div>
            ) : (
              notifications.map(item => {
                if (item.kind === 'tat') {
                  const { patientName, patientId, modality, testName } =
                    formatTatNotificationContent(item.data);
                  return (
                    <TatNotificationListItem
                      key={`tat-${item.data.id}`}
                      onActivate={() => handleNotificationClick(item)}
                      onDismiss={event => void handleDismiss(event, item)}
                    >
                      <div className="tat-notification-item-heading">TAT Breach Alert</div>
                      <div className="tat-notification-item-line">
                        Patient:{' '}
                        <strong>
                          {patientName} ({patientId})
                        </strong>
                      </div>
                      <div className="tat-notification-item-line">
                        Modality: <strong>{modality}</strong>
                      </div>
                      <div className="tat-notification-item-line">
                        Test: <strong>{testName}</strong>
                      </div>
                      <div className="tat-notification-item-footer">
                        TAT exceeded. Please review immediately.
                      </div>
                      <div className="tat-notification-item-time">
                        {formatRelativeTime(item.createdAt)}
                      </div>
                    </TatNotificationListItem>
                  );
                }

                const peer = item.data;
                const meta = peer.metadata || {};
                return (
                  <TatNotificationListItem
                    key={`peer-${peer.id}`}
                    onActivate={() => handleNotificationClick(item)}
                    onDismiss={event => void handleDismiss(event, item)}
                  >
                    <div className="tat-notification-item-heading">
                      {getPeerReviewEventLabel(peer.event_type)}
                    </div>
                    <div className="tat-notification-item-line">{peer.message}</div>
                    {(meta.patient_name || meta.accession_number) && (
                      <div className="tat-notification-item-line">
                        {meta.patient_name && (
                          <>
                            Patient: <strong>{meta.patient_name}</strong>
                          </>
                        )}
                        {meta.accession_number && (
                          <>
                            {meta.patient_name ? ' · ' : ''}
                            Acc: <strong>{meta.accession_number}</strong>
                          </>
                        )}
                      </div>
                    )}
                    <div className="tat-notification-item-time">
                      {formatRelativeTime(item.createdAt)}
                    </div>
                  </TatNotificationListItem>
                );
              })
            )}
          </div>
        </dialog>
      ) : null}
    </div>
  );
};

export default TatNotificationBell;
